import * as support from "./ParticipationTable.support";
import PieChartRoundedIcon from "@mui/icons-material/PieChartRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import { Typography, Box, Stack, Tooltip } from "@mui/material";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { DataGrid } from "@mui/x-data-grid";
import * as colors from "common/colors";
import * as numbers from "common/numbers";
import Component from "components/Component";
import * as labels from "config.labels";
import { useState } from "react";
import { useSelector } from "react-redux";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as towerSelectors from "store/selectors/pricing/tower/towerPricingSelectors";

const CustomLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  carrier,
  value,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline={"central"}
      fontSize={12}
    >
      <tspan x={x} dy={"-1.2em"}>
        {`${carrier}:`}
      </tspan>
      <tspan x={x} dy={"1.2em"}>
        {`$${numbers.abbreviated(value)}`}
      </tspan>
      <tspan x={x} dy={"1.2em"}>
        {`(${(percent * 100).toFixed(1)}%)`}
      </tspan>
    </text>
  );
};

const Chart = ({ carriersData }) => {
  const [dataKey, setDataKey] = useState("totalLimit");

  const data = (carriersData ?? []).filter(
    (carrier) => (carrier?.[dataKey] ?? 0) > 0
  );

  const numberOfCarriers = data.length;
  const palette = colors.shades("#ffe0b2", "#ff6f00", numberOfCarriers);

  return (
    <>
      <ResponsiveContainer width={"100%"} height={400}>
        <PieChart>
          <Pie
            data={data}
            dataKey={dataKey}
            nameKey={"carrier"}
            cx={"50%"}
            cy={"50%"}
            outerRadius={150}
            labelLine={false}
            label={CustomLabel}
            isAnimationActive={false}
          >
            {data.map((carrier, index) => (
              <Cell
                key={`cell-${index}`}
                fill={palette[index % palette.length]}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <Box display={"flex"} justifyContent={"center"}>
        <ToggleButtonGroup
          value={dataKey}
          exclusive
          onChange={(e, newDataKey) => setDataKey(newDataKey)}
          color={"primary"}
          size={"small"}
          aria-label={"data key toggle"}
        >
          <ToggleButton value={"totalLimit"} aria-label={"Total Limit"}>
            <span style={{ paddingRight: "3px" }}>{"Total Limit"}</span>
          </ToggleButton>
          <ToggleButton value={"totalPremium"} aria-label={"Total Premium"}>
            <span style={{ paddingRight: "2px" }}>{"Total Premium"}</span>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </>
  );
};

const CarrierParticipation = ({ companyName }) => {
  const resolvedTower = useSelector(towerSelectors.selectResolvedTower);

  const carriersData = support.buildCarriersData(resolvedTower?.layers ?? []);

  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const [view, setView] = useState("Table");

  if (!carriersData || carriersData.length === 0) {
    return (
      <Component
        title={translateLabel(labels.PARTICIPATION_TABLE_TITLE, {
          default: "Carrier Participation",
        })}
        lg_width={6}
        md_width={12}
      >
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          height={"100%"}
          padding={2}
        >
          <Typography variant={"h6"} color={"textSecondary"}>
            {translateLabel(labels.PARTICIPATION_TABLE_NO_DATA, {
              default: "No Tower Data",
            })}
          </Typography>
        </Box>
      </Component>
    );
  }
  const updatedCarriersData = carriersData.map((carrier) => {
    if (carrier.carrier === "Us") {
      return { ...carrier, carrier: companyName || "Our Share" };
    }
    return carrier;
  });

  const handleViewChange = (event, newView) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  return (
    <Component
      title={translateLabel(labels.PARTICIPATION_TABLE_TITLE, {
        default: "Carrier Participation",
      })}
      lg_width={6}
      md_width={12}
      options={
        <Stack direction={"row"} alignItems={"center"} spacing={2}>
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            color={"primary"}
            size={"small"}
            aria-label={"view toggle"}
          >
            <Tooltip title={"Show Table"} arrow>
              <ToggleButton value={"Table"} aria-label={"Show Table"}>
                <TableChartRoundedIcon
                  fontSize={"small"}
                  sx={{ marginRight: "3px" }}
                />
                <span style={{ paddingRight: "3px" }}>{"Table"}</span>
              </ToggleButton>
            </Tooltip>
            <Tooltip title={"Show Chart"} arrow>
              <ToggleButton value={"Chart"} aria-label={"Show Chart"}>
                <PieChartRoundedIcon
                  fontSize={"small"}
                  sx={{ marginRight: "3px" }}
                />
                <span style={{ paddingRight: "2px" }}>{"Chart"}</span>
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Stack>
      }
    >
      {view === "Chart" ? (
        <Chart carriersData={updatedCarriersData} />
      ) : (
        <DataGrid
          rows={updatedCarriersData.map((carrier, index) => ({
            id: index,
            carrier: carrier.carrier,
            totalLimit: carrier.totalLimit,
            totalPremium: carrier.totalPremium,
          }))}
          columns={[
            {
              field: "carrier",
              headerName: translateLabel(labels.PARTICIPATION_TABLE_CARRIER, {
                default: "Carrier",
              }),
              flex: 1,
              minWidth: 150,
            },
            {
              field: "totalLimit",
              headerName: translateLabel(
                labels.PARTICIPATION_TABLE_TOTAL_LIMIT,
                {
                  default: "Total Limit ($)",
                }
              ),
              type: "number",
              flex: 1,
              minWidth: 150,
              align: "right",
              headerAlign: "right",
            },
            {
              field: "totalPremium",
              headerName: translateLabel(
                labels.PARTICIPATION_TABLE_TOTAL_PREMIUM,
                {
                  default: "Total Premium ($)",
                }
              ),
              type: "number",
              flex: 1,
              minWidth: 150,
              align: "right",
              headerAlign: "right",
            },
          ]}
          initialState={{
            sorting: {
              sortModel: [{ field: "totalLimit", sort: "desc" }],
            },
          }}
          autoHeight
          disableColumnMenu={false}
          disableSelectionOnClick
          hideFooter={updatedCarriersData.length <= 10}
          pageSize={10}
        />
      )}
    </Component>
  );
};

export default CarrierParticipation;
